import { CM_PER_PIXEL, INCH_PER_PIXEL, PIXEL_PER_CM, PIXEL_PER_INCH } from '../constants/units';
import formatQuantity from 'format-quantity';
import numericQuantity from 'numeric-quantity';

const FRACTION_CHARACTERS = { '1/2': '½', '1/4': '¼', '3/4': '¾', '1/8': '⅛', '3/8': '⅜', '5/8': '⅝', '7/8': '⅞' };

export default class MathHelper {
  static fractionGreaterThan8ths(value) {
    return value.includes("/") && parseInt(value.substring(value.indexOf("/")+1)) > 8;
  }

  static validUnit(value) {
    return /^-{0,1}\d*\.{0,1}\d+$/i.test(value);
  }

  static inchesToPixels(inchString) {
    return Math.round(numericQuantity(inchString) * PIXEL_PER_INCH);
  }

  static cmToPixels(cmString) {
    return Math.round(parseFloat(cmString) * PIXEL_PER_CM);
  }

  static unitsToPixels(value, unit) {
    if (unit === 'cm') {
      return this.cmToPixels(value);
    } else {
      return this.inchesToPixels(value);
    }
  }

  /*
   * If forDisplay is passed, the number is returned like so '⅓' instead of '1/2' 
   */
  static pixelsToInches(value, forDisplay = false) {
    return formatQuantity(value/PIXEL_PER_INCH, forDisplay);
  }

  /**
   * Inches to the nearest 1/16, as a mixed number such as "28 3/8", for showing a measurement.
   * Pixel rounding leaves values like 28.347 that are not exactly a fraction, and the plain
   * conversion above would show those as long decimals.
   * @param {number} value pixels
   * @param {boolean} forDisplay true to use ¼ ½ ¾ and the eighths where there is a character for them
   *   (other sixteenths are written as "11/16"); false for plain "1/4" text that can be typed back in
   */
  static pixelsToInchesFraction(value, forDisplay = false) {
    const total = Math.round((Math.abs(value) / PIXEL_PER_INCH) * 16);
    const whole = Math.floor(total / 16);
    let numerator = total % 16;
    let denominator = 16;
    while (numerator > 0 && numerator % 2 === 0) {
      numerator /= 2;
      denominator /= 2;
    }
    const sign = value < 0 && total > 0 ? '-' : '';
    if (numerator === 0) return `${sign}${whole}`;

    const fraction = `${numerator}/${denominator}`;
    const character = forDisplay ? FRACTION_CHARACTERS[fraction] : undefined;
    if (character) return `${sign}${whole || ''}${character}`;
    return `${sign}${whole ? `${whole} ` : ''}${fraction}`;
  }

  static pixelsToCm(value) {
    return (value/PIXEL_PER_CM).toFixed(1);
  }

  static pixelsToUnits(value, unit, forDisplay = false) {
    if (unit === 'cm') {
      return this.pixelsToCm(value) + (forDisplay ? ' cm' : '');
    } else {
      return this.pixelsToInches(value, forDisplay) + (forDisplay ? '"' : '');
    }
  }

  /**
   * this function performs the conversion of a number depending on the chosen conversion unit
   * @param {number} value value to convert
   * @param {number} unit conversion unit
   * @returns {number} converted value
   */
  static convertValue(value, unit) {
    return value * unit;
  }

  /**
   * find the rational from a decimal
   * @param {number} value returns the rational number [numerator, denominator]
   */
  static findRational(value) {
    let bestN = 1;
    let bestD = 1;
    const isNegative = value < 0;
    let bestResult = isNegative ? Math.abs(value + (bestN / bestD)) : Math.abs(value - (bestN / bestD));
    let denominator = 2;

    for (let i = 0; bestResult > 0 && i < 4; i += 1) {
      let numerator = 0;
      let result = 0;

      for (let j = 1; j < denominator; j += 1) {
        numerator = j;
        result = isNegative ? Math.abs(value + (numerator / denominator)) : Math.abs(value - (numerator / denominator));

        if (result < bestResult) {
          bestN = numerator;
          bestD = denominator;
          bestResult = result;
        }
      }

      denominator *= 2;
    }
    return [bestN, bestD];
  }

  /**
   * this function performs the conversion of a number depending on the chosen conversion unit
   * @param {number} value value to convert
   * @param {string} unit conversion unit
   * @returns {Array} an array with the converted value [int part, rational part 1, rational part 2, unit]
   */
  static convertToUnits(value, unit) {
    if (unit === 'cm') {
      return [MathHelper.round(MathHelper.convertValue(value, CM_PER_PIXEL), 1), '', '', 'cm'];
    }

    let val = 0;
    val = MathHelper.convertValue(value, INCH_PER_PIXEL);
    if (val === 0) {
      return [val, '', '', '"'];
    }
    const isNegative = val < 0;
    const decimal = String(val).split('.');
    let intPart = decimal[0] * 1;
    const decimalPart = `0.${decimal[1]}` * 1;
    let rationalPart = 0;

    if (intPart === 0) {
      rationalPart = MathHelper.findRational(val);

      if (rationalPart[1] === 1) {
        intPart = isNegative ? -1 : 1;
        return [intPart, '', '', '"'];
      }

      const numerator = isNegative ? `-${rationalPart[0]}` : `${rationalPart[0]}`;

      return ['', numerator, rationalPart[1], '"'];
    }

    if (Number.isNaN(decimalPart)) {
      return `${intPart}"`;
    }

    rationalPart = MathHelper.findRational(decimalPart);

    if (rationalPart[0] === 1 && rationalPart[1] === 1) {
      intPart += 1;
      return [intPart, '', '', '"'];
    }

    return [intPart, rationalPart[0], rationalPart[1], '"'];
  }

  static convertToUnitsStr(value, unit) {
    const convert = this.convertToUnits(value, unit);
    if (convert[1] === '') {
      return `${convert[0]}`;
    } else if (convert[1] === convert[2]) {
      const result = convert[0] > 0 ? (Number(convert[0]) + 1).toString() : (Number(convert[0]) - 1).toString();
      return result;
    } else {
      return `${convert[0]} ${convert[1]}/${convert[2]}`;
    }
    return "";
  } 


  /**
   * this function performs the conversion of a number in a unit to pixels
   * @param {string} value value to convert
   * @param {string} unit conversion unit
   * @returns {number} converted value
   */
  static convertToPixels(value, unit) {
    if (unit === 'cm') {
      return MathHelper.convertValue(value, PIXEL_PER_CM);
    }

    // Inches may be typed with a fraction, such as "5 3/16" or "-1/2".
    if (String(value).includes('/')) {
      const inches = MathHelper.parseFractionalInches(value);
      return inches === null ? null : MathHelper.convertValue(inches, PIXEL_PER_INCH);
    }
    return MathHelper.convertValue(value, PIXEL_PER_INCH);
  }

  /**
   * Reads a length typed in inches: a whole or decimal number, a fraction, or a whole number and a
   * fraction, with an optional minus sign. "5 3/16" is 5.1875 and "-2 1/2" is -2.5.
   * @param {string} value the text typed by the user
   * @returns {number|null} the length in inches, or null if the text is not a length
   */
  static parseFractionalInches(value) {
    const match = /^\s*(-)?\s*(?:(\d+(?:\.\d+)?)(?:\s+(\d+)\s*\/\s*(\d+))?|(\d+)\s*\/\s*(\d+))\s*$/.exec(String(value));
    if (!match) return null;
    const [, minus, whole, numerator, denominator, onlyNumerator, onlyDenominator] = match;
    const top = Number(numerator || onlyNumerator || 0);
    const bottom = Number(denominator || onlyDenominator || 1);
    if (bottom === 0) return null;
    const inches = Number(whole || 0) + top / bottom;
    return minus ? -inches : inches;
  }

  /**
   * round the values of a point {x, y}
   * @param {Object} point {x, y}
   */
  static roundPoint(point) {
    const p = point;

    if (Number.isNaN(p.x) || Number.isNaN(p.y)) return p;

    p.x = Math.round(p.x);
    p.y = Math.round(p.y);

    return p;
  }

  /**
   * Sum two points
   * @param {Object} startPoint {x, y}
   * @param {Object} endPoint {x, y}
   */
  static addPoints(startPoint, endPoint) {
    return {
      x: startPoint.x + endPoint.x,
      y: startPoint.y + endPoint.y,
    };
  }

  /**
   * subtract two points
   * @param {Object} endPoint {x, y}
   * @param {Object} startPoint {x, y}
   */
  static substractPoints(endPoint, startPoint) {
    return {
      x: endPoint.x - startPoint.x,
      y: endPoint.y - startPoint.y,
    };
  }

  /**
   * calculate the distance between two points
   * @param {Object} startPoint {x, y}
   * @param {Object} endPoint {x, y}
   */
  static getDistance(startPoint, endPoint) {
    const x = endPoint.x - startPoint.x;
    const y = endPoint.y - startPoint.y;

    return Math.hypot(x, y);
  }


  static interpolatePointsCorrect(startPoint, endPoint, f) {
    const x = startPoint.x + (endPoint.x - startPoint.x) * f;
    const y = startPoint.y + (endPoint.y - startPoint.y) * f;
    return { x, y };
  }


  /**
   * interpolates two points according to a proportion
   * @param {Object} startPoint {x, y}
   * @param {Object} endPoint {x, y}
   * @param {number} f proportion
   */
  static interpolatePoints(startPoint, endPoint, f) {
    const x = (endPoint.x - startPoint.x) * f;
    const y = (endPoint.y - startPoint.y) * f;
    return { x, y };
  }

  /**
   * move a point in relation to another point taking into account a distance
   * @param {Object} startPoint {x, y}
   * @param {Object} endPoint {x, y}
   * @param {number} distance
   */
  static movePoint(startPoint, endPoint, distance) {
    const d = this.getDistance(startPoint, endPoint);
    const f = distance / d;
    return this.interpolatePoints(endPoint, startPoint, f);
  }

  static scalePoint(point, scale) {
    return { ...point, x: point.x * scale, y: point.y * scale };
  }

  /**
   * Set position to an object in relation to a point
   * @param {Object} object
   * @param {Object} point {x, y}
   */
  static setPosition(object, point) {
    const o = object;
    o.x -= point.x;
    o.y -= point.y;
    return o;
  }

  /**
   * get the magnitude of a vector
   * @param {Object} vector {x, y}
   */
  static getMagnitude(vector) {
    const { x, y } = vector;
    return Math.sqrt((x * x) + (y * y));
  }

  /**
   * get Normalized Vector
   * @param {Object} vector
   */
  static getNormalizedVector(vector) {
    const magnitude = this.getMagnitude(vector);
    return { x: vector.x / magnitude, y: vector.y / magnitude };
  }

  /**
   * get the angle between a coordinate x y
   * @param {number} x
   * @param {number} y
   */
  static getAngle(x, y) {
    return Math.atan2(y, x);
  }

  /**
   * get the angle between two points
   * @param {Object} startPoint
   * @param {Object} endPoint
   */
  static getAnglePoints(startPoint, endPoint) {
    const x = endPoint.x - startPoint.x;
    const y = endPoint.y - startPoint.y;

    return this.getAngle(x, y);
  }

  /**
   * convert any angle to the equivalent angle between 0 and 360 (degrees) or 0 and 2PI (radians)
   * @return {number}
   */
  static getOneTurnPositiveAngle(angle, degrees = false) {
    let oneTurnPositiveAngle = degrees ? this.convertDegreesToRadians(angle) : angle;

    oneTurnPositiveAngle %= (2 * Math.PI);

    if (oneTurnPositiveAngle < 0) {
      oneTurnPositiveAngle += (2 * Math.PI);
    }

    return degrees ? this.convertRadiansToDegrees(oneTurnPositiveAngle) : oneTurnPositiveAngle;
  }

  /**
   * @return {number}
   * get angle between three points
   * @param {*} c center point
   * @param {*} p0
   * @param {*} p1
   */
  static getAngle3Points(c, p0, p1) {
    const p0c = Math.hypot(c.x - p0.x, c.y - p0.y);
    const p1c = Math.hypot(c.x - p1.x, c.y - p1.y);
    const p0p1 = Math.hypot(p1.x - p0.x, p1.y - p0.y);

    return Math.acos((((p1c * p1c) + (p0c * p0c)) - (p0p1 * p0p1)) / (2 * p1c * p0c));
  }

  /**
   * get the angle between two points in degress
   * @param {Object} startPoint
   * @param {Object} endPoint
   */
  static getAnglePointsDegrees(startPoint, endPoint) {
    let angle = this.getAnglePoints(startPoint, endPoint);
    angle = MathHelper.convertRadiansToDegrees(angle);

    return this.getOneTurnPositiveAngle(angle, true);
  }

  /**
   * get angle between two lines
   * @param {Object} line1
   * @param {Object} line2
   */
  static getAngleLines(line1, line2) {
    const angle1 = this.getAnglePoints(line1.p1, line1.p2);
    const angle2 = this.getAnglePoints(line2.p1, line2.p2);
    const angle = angle2 - angle1;

    return this.getOneTurnPositiveAngle(angle);
  }

  /**
   *
   * @param {number} distance
   * @param {number} angle
   */
  static getOppositeLength(distance, angle) {
    return distance * Math.sin(angle);
  }

  /**
   *
   * @param {number} distance
   * @param {number} angle
   */
  static getAdjacentLength(distance, angle) {
    return distance * Math.cos(angle);
  }

  /**
   *
   * @param {number} angle
   */
  static convertDegreesToRadians(angle) {
    return (angle * Math.PI) / 180;
  }

  /**
   * Converts the passed in radians to degrees less than 360
   * @param {number} angle
   */
  static convertRadiansToDegrees(angle) {
    return ((angle * 180) / Math.PI) % 360;
  }

  /**
   * rounds a number according to the precision of decimals
   * @param {number} number
   * @param {number} decimals
   */
  static round(number, decimals) {
    if (Number.isNaN(number)) return number;

    const x = 10 ** decimals;
    return Math.round(number * x) / x;
  }

  /**
   *
   * @param {number} numberOne
   * @param {number} numberTwo
   * @param {number} decimals
   */
  static equals(numberOne, numberTwo, decimals = 0) {
    const nOne = parseFloat(numberOne);
    const nTwo = parseFloat(numberTwo);
    return this.round(nOne, decimals) === this.round(nTwo, decimals);
  }

  /**
   * check if two numbers are not equals
   * @param {number} numberOne
   * @param {number} numberTwo
   */
  static notEquals(numberOne, numberTwo) {
    const nOne = parseFloat(numberOne);
    const nTwo = parseFloat(numberTwo);
    return nOne !== nTwo;
  }

  /**
   *
   * @param {number} vectorOne
   * @param {number} vectorTwo
   */
  static getDotProduct(vectorOne, vectorTwo) {
    return (vectorOne.x * vectorTwo.x) + (vectorOne.y * vectorTwo.y);
  }

  /**
   *
   * @param {Object} object
   * @param {number} scalar
   */
  static scalarMultiplication(object, scalar = 1) {
    const newObject = {};

    Object.keys(object).forEach((key) => {
      if (object[key] !== null && typeof object[key] !== 'undefined' && !Number.isNaN(object[key])) {
        newObject[key] = object[key] * scalar;
      } else {
        newObject[key] = object[key];
      }
    });

    return newObject;
  }

  /**
   *
   * @param {Object} point
   * @param {number} scalar
   */
  static scalarMultiplicationPoint(point, scalar) {
    return {
      ...point,
      x: point.x * scalar,
      y: point.y * scalar,
    };
  }

  /**
   *
   * @param {Object} vector
   * @param {number} scalar
   */
  static scalarDivision(vector, scalar = 1) {
    if (Number.isNaN(vector.x) || Number.isNaN(vector.y)) {
      return vector;
    }

    return {
      ...vector,
      x: MathHelper.scalarDivisionOfValue(vector.x, scalar),
      y: MathHelper.scalarDivisionOfValue(vector.y, scalar),
    };
  }

  /**
   *
   * @param {number} value
   * @param {number} scale
   */
  static scalarDivisionOfValue(value, scale) {
    return value / scale;
  }

  /**
   *
   * @param {Object} startPoint
   * @param {Object} endPoint
   * @param {string} unit
   */
  static getPointsRuler(startPoint, endPoint, unit) {
    const length = MathHelper.getDistance(startPoint, endPoint);
    let lengthSegment = 0;

    if (unit === 'cm') {
      lengthSegment = (length - 40) / 2;
    } else {
      lengthSegment = (length - 30) / 2;
    }

    const angle = MathHelper.getAnglePoints(startPoint, endPoint);
    const x = Math.cos(angle) * (lengthSegment);
    const y = Math.sin(angle) * (lengthSegment);
    return { start: { x: x + startPoint.x, y: y + startPoint.y }, end: { x: endPoint.x - x, y: endPoint.y - y } };
  }

  /**
   *
   * @param {Object} point
   * @param {Object} axisStart
   * @param {Object} axisEnd
   */
  static reflectPoint(point, axisStart, axisEnd) {
    if (point.x === null || Number.isNaN(point.x)
      || point.y === null || Number.isNaN(point.y)) {
      return { ...point, x: NaN, y: NaN };
    }

    const deltaX = axisEnd.x - axisStart.x;
    const deltaY = axisEnd.y - axisStart.y;

    if (deltaX === 0 && deltaY === 0) {
      return { x: (2 * axisStart.x) - point.x, y: (2 * axisStart.y) - point.y };
    }

    const t = (((point.x - axisStart.x) * deltaX) + ((point.y - axisStart.y) * deltaY)) / ((deltaX * deltaX) + (deltaY * deltaY));
    const x = (2 * (axisStart.x + (t * deltaX))) - point.x;
    const y = (2 * (axisStart.y + (t * deltaY))) - point.y;
    return { ...point, x, y };
  }

  /**
   *
   * @param {Array} points
   * @param {Object} referencePoint
   * @param {number} thresholdLimit
   */
  static findClosestPoint(points, referencePoint, thresholdLimit) {
    return points.find((point) => MathHelper.getDistance(point, referencePoint) < thresholdLimit);
  }

  /**
   * Uses cosine law to get the opposite angle of a given 'distance' having the 2 other sides of the triangle equal to 'length'.
   */
  static getAngleForOppositeDistance(distance, length) {
    const angle = 1 - ((distance ** 2) / (2 * (length ** 2)));

    return Math.acos(angle);
  }

  /**
   * Uses cosine law to get the opposite distance of a given 'angle' having the 2 other sides of the triangle equal to 'length'.
   */
  static getDistanceForOppositeAngle(angle, length) {
    return length * (Math.sqrt(2 * (1 - Math.cos(angle))));
  }

  /**
   * Get quadrant number.
   * @param {number} angle
   * @returns {number} quadrant number (1 - 4) or 0 (not found).
   */
  static getQuadrant(angle) {
    const axes = [
      0,
      Math.PI / 2,
      Math.PI,
      3 * (Math.PI / 2),
      2 * Math.PI,
    ];

    return axes.slice(1).findIndex((axis, idx) => angle >= axes[idx] && angle <= axis) + 1;
  }

  /**
   * Get reference angle.
   * @param {number} angle
   * @returns {number} angle reference or angle parameter.
   */
  static getReferenceAngle(angle) {
    const quadrant = this.getQuadrant(angle) || 1;
    return [
      angle,
      Math.PI - angle,
      angle - Math.PI,
      (2 * Math.PI) - angle,
    ][quadrant - 1];
  }

  /**
   * Get nearest axis.
   * @param {number} angle
   * @returns {number} nearest axis.
   */
  static getAlignTargetAngle(angle) {
    const referenceAngle = this.getReferenceAngle(angle);
    const quadrant = this.getQuadrant(angle) || 1;
    const quadrantAxes = [
      { h: 0, v: Math.PI / 2 }, // horizontal (h) and vertical (v)
      { h: Math.PI, v: Math.PI / 2 },
      { h: Math.PI, v: 3 * (Math.PI / 2) },
      { h: 0, v: 3 * (Math.PI / 2) },
    ];
    const quadAxis = quadrantAxes[quadrant - 1];

    return referenceAngle < Math.PI / 4 ? quadAxis.h : quadAxis.v;
  }

  static areBoundsIntersected({ x: x1, y: y1, w: w1, h: h1 }, { x: x2, y: y2, w: w2, h: h2 }) {
    const areNotIntersected = x1 + w1 < x2 || x2 + w2 < x1 || y1 + h1 < y2 || y2 + h2 < y1;

    return !areNotIntersected;
  }

  /**
   * Converte distance and angle to x and y.
   * @param { number } distance
   * @param { number } angle
   */
  static convertPolarToCartesianCoordinates(distance, angle) {
    const x = this.getAdjacentLength(distance, angle);
    const y = this.getOppositeLength(distance, angle);

    return { x, y };
  }

  /**
   * Convert x and y to distance and angle.
   * @param { number } x
   * @param { number } y
   */
  static convertCartesianToPolarCoordinates(x, y) {
    const distance = Math.hypot(x, y);
    const angle = this.getAngle(x, y);

    return { distance, angle };
  }

  static modulo(a, b) {
    return ((a % b) + b) % b;
  }
}
